<template>
  <modal class="style-modal-wide" @close-modal="close">
    <main class="feedback-modal mt-1">
      <div class="container">
        <template v-if="!sent">
          <h2 class="my-0 pb-2">Send us feedback</h2>
          <p class="feedback-intro">
            How has your experience been so far? Tell us what is working and
            what is not. We read every one of these.
          </p>
          <textarea
            v-model="message"
            class="feedback-input"
            rows="6"
            maxlength="2000"
            placeholder="What would you like us to know?"
          ></textarea>
          <div class="feedback-counter">{{ message.length }} / 2000</div>
          <div class="d-flex justify-content-center mt-3">
            <button
              :disabled="!message.trim() || sending"
              type="submit"
              class="btn btn-primary mb-2 bold"
              @click="submit()"
            >
              <b>{{ sending ? 'Sending...' : 'Send feedback' }}</b>
            </button>
          </div>
        </template>
        <div v-else class="feedback-sent">
          <font-awesome-icon :icon="faCircleCheck" size="2x" />
          <h2 class="my-0 pt-3 pb-2">Thank you</h2>
          <p class="feedback-intro">
            Your feedback is with the team. We appreciate you taking the time.
          </p>
          <div class="d-flex justify-content-center mt-3">
            <button
              type="button"
              class="btn btn-primary mb-2 bold"
              @click="close()"
            >
              <b>Close</b>
            </button>
          </div>
        </div>
      </div>
    </main>
  </modal>
</template>

<script setup>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons/faCircleCheck';
import Modal from '@/components/global/WideModal.vue';
import { useAPI } from '@/composables/useAPI';
import { feedbackSuccess, feedbackFail } from '@/core/notifications';

const emit = defineEmits(['closeModal']);

const route = useRoute();
const { request: submitFeedbackApi } = useAPI('user/submitFeedback/');

const message = ref('');
const sending = ref(false);
const sent = ref(false);

function close() {
  emit('closeModal');
}

async function submit() {
  if (sending.value) return;
  const text = message.value.trim();
  if (!text) return;
  sending.value = true;
  try {
    // `page` is context for whoever reads the Telegram post, so send the
    // matched route path rather than the resolved URL: no ids, no query.
    await submitFeedbackApi({ message: text, page: route.path });
    sent.value = true;
    feedbackSuccess();
  } catch (error) {
    feedbackFail(error?.message || 'Please try again later');
  } finally {
    sending.value = false;
  }
}
</script>

<style scoped lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/theme/variables.scss';

.feedback-modal {
  color: $black;
  text-align: center;

  .feedback-intro {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-bottom: 1rem;
  }

  // Mirrors .deposit-input so the widget matches the rest of the app's
  // inputs in both light and night themes.
  .feedback-input {
    width: 100%;
    border: 1px solid var(--grey-1200-1);
    border-radius: 10px;
    padding: 13px 16px;
    background: var(--white_01);
    color: var(--color-primary_01);
    font-size: 15px;
    font-weight: 400;
    font-family: inherit;
    resize: vertical;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &::placeholder {
      color: var(--grey-2200);
    }
  }

  .feedback-counter {
    font-size: 0.75rem;
    opacity: 0.6;
    text-align: right;
    margin-top: 0.35rem;
  }

  .feedback-sent {
    padding: 1rem 0;
  }
}
</style>
